// frontend/src/components/wizard/StepMapping/ImportPreviewModal.jsx
import React from 'react';
import { X, Check, AlertTriangle, XCircle, Upload } from 'lucide-react';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { TRANSFORM_RULES } from '../../../utils/transformRules';

/** Serializa una regla a string legible: SET_FIXED(100), FORMAT_DATE(YYYY-MM-DD, DD/MM/YYYY) */
function formatRule(t) {
  const id = t.id || t.label || '';
  const rule = TRANSFORM_RULES.find(r => r.id === id);
  const paramDefs = rule?.params || [];
  if (paramDefs.length === 0 || !t.params) return id;
  const vals = paramDefs.map(k => t.params[k]).filter(v => v !== '' && v != null);
  return vals.length > 0 ? `${id}(${vals.join(', ')})` : id;
}

/**
 * Modal de preview que muestra el resultado de la validación del archivo importado.
 *
 * Props:
 *   isOpen             — boolean
 *   onClose            — () => void
 *   onConfirm          — () => void   (aplica el mapping al localMap)
 *   validationResult   — { matched, unmatchedSource, unmatchedTarget, summary }
 *   fileName           — nombre del archivo subido
 */
export function ImportPreviewModal({ isOpen, onClose, onConfirm, validationResult, fileName }) {
  const { t } = useLanguageStore();

  // ESC cierra el modal
  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !validationResult) return null;

  const { matched, unmatchedSource, unmatchedTarget, summary } = validationResult;
  const hasMatches = matched.length > 0;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-black text-slate-900">
              {t('wizard.mapping.import_preview_title') || 'Vista previa de importación'}
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">{fileName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Summary badges */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-help"
            title={t('wizard.mapping.import_matched_tip') || 'Campos que coinciden en origen y destino. Se aplicarán al confirmar.'}
          >
            <Check className="w-3.5 h-3.5" />
            {summary.matched} {t('wizard.mapping.import_matched') || 'coincidencias'}
          </span>
          {summary.unknownRules > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg bg-orange-100 text-orange-700 border border-orange-200 cursor-help"
              title={t('wizard.mapping.import_unknown_rules_tip') || 'Reglas de transformación que no existen en DatiaFlow. Se importarán pero no tendrán efecto en la migración.'}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {summary.unknownRules} {t('wizard.mapping.import_unknown_rules') || 'reglas no reconocidas'}
            </span>
          )}
          {matched.some(e => e.typeWarning) && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg bg-amber-50 text-amber-600 border border-amber-200 cursor-help"
              title={t('wizard.mapping.import_type_mismatch_tip') || 'El tipo de dato del archivo difiere del esquema real. Solo informativo — DatiaFlow convierte tipos automáticamente.'}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {matched.filter(e => e.typeWarning).length} {t('wizard.mapping.import_type_mismatch') || 'tipo diferente'}
            </span>
          )}
          {summary.noSource > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg bg-amber-100 text-amber-700 border border-amber-200 cursor-help"
              title={t('wizard.mapping.import_no_source_tip') || 'El campo origen del archivo no existe en la conexión actual. Quedarán como "sin mapear" para asignar manualmente.'}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {summary.noSource} {t('wizard.mapping.import_no_source') || 'sin origen'}
            </span>
          )}
          {summary.noTarget > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg bg-red-100 text-red-600 border border-red-200 cursor-help"
              title={t('wizard.mapping.import_no_target_tip') || 'La columna destino no existe en la tabla actual. Estas filas se descartarán.'}
            >
              <XCircle className="w-3.5 h-3.5" />
              {summary.noTarget} {t('wizard.mapping.import_no_target') || 'descartadas'}
            </span>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Matched entries */}
          {matched.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
                <Check className="w-4 h-4" />
                {t('wizard.mapping.import_will_apply') || 'Se aplicarán'}
              </h4>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 text-xs">
                      <th className="text-left px-3 py-2 font-bold">{t('wizard.mapping.col_target') || 'Destino'}</th>
                      <th className="text-left px-3 py-2 font-bold">{t('wizard.mapping.col_source') || 'Origen'}</th>
                      <th className="text-left px-3 py-2 font-bold">{t('wizard.mapping.col_transforms') || 'Reglas'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matched.map((entry, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-800">
                          {entry.targetField}
                          {entry.typeWarning && (
                            <span
                              className="ml-1.5 text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200"
                              title={`${t('wizard.mapping.import_type_expected') || 'Tipo esperado'}: ${entry.actualType}`}
                            >
                              {entry.dataType} ≠ {entry.actualType}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs text-indigo-600">
                          {entry.ignore ? (
                            <span className="text-slate-400 italic">IGNORE</span>
                          ) : (
                            entry.sourceField
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-slate-500">
                          {entry.transformations?.length > 0
                            ? entry.transformations.map((tr, ti) => {
                              const isUnknown = entry.unknownTransforms?.includes(tr.id);
                              return (
                                <React.Fragment key={ti}>
                                  {ti > 0 && <span className="text-slate-300"> → </span>}
                                  <span className={isUnknown ? 'text-orange-600 line-through' : ''}>
                                    {formatRule(tr)}
                                  </span>
                                </React.Fragment>
                              );
                            })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Unmatched source (target exists but source field not found) */}
          {unmatchedSource.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-amber-700 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />
                {t('wizard.mapping.import_source_missing') || 'Origen no encontrado — quedarán sin mapear'}
              </h4>
              <div className="border border-amber-200 rounded-xl overflow-hidden bg-amber-50/30">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-amber-50 text-amber-700 text-xs">
                      <th className="text-left px-3 py-2 font-bold">{t('wizard.mapping.col_target') || 'Destino'}</th>
                      <th className="text-left px-3 py-2 font-bold">{t('wizard.mapping.import_source_expected') || 'Origen esperado'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedSource.map((entry, idx) => (
                      <tr key={idx} className="bg-white/60">
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-800">{entry.targetField}</td>
                        <td className="px-3 py-1.5 font-mono text-xs text-amber-600 line-through">{entry.sourceField}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Unmatched target (field doesn't exist in current target) */}
          {unmatchedTarget.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-red-600 mb-2 flex items-center gap-1.5">
                <XCircle className="w-4 h-4" />
                {t('wizard.mapping.import_target_missing') || 'Columna destino no existe — se descartarán'}
              </h4>
              <div className="border border-red-200 rounded-xl overflow-hidden bg-red-50/30">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-red-50 text-red-600 text-xs">
                      <th className="text-left px-3 py-2 font-bold">{t('wizard.mapping.col_target') || 'Destino'}</th>
                      <th className="text-left px-3 py-2 font-bold">{t('wizard.mapping.col_source') || 'Origen'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedTarget.map((entry, idx) => (
                      <tr key={idx} className="bg-white/60">
                        <td className="px-3 py-1.5 font-mono text-xs text-red-500 line-through">{entry.targetField}</td>
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-400">{entry.sourceField}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Tip de formato — siempre visible */}
        {/* <div className="px-6 py-3 border-t border-slate-100 bg-blue-50/50">
          <details className="text-xs text-slate-600">
            <summary className="font-bold text-blue-700 cursor-pointer hover:text-blue-800">
              {t('wizard.mapping.import_format_tip_title') || '¿Cómo armar el archivo?'}
            </summary>
            <div className="mt-2 space-y-1.5 text-[11px] leading-relaxed">
              <p>
                <strong>Excel:</strong> {t('wizard.mapping.import_format_excel') || 'Columnas obligatorias: Source, Target. Opcionales: Data Type, Transformations, Ignored. En modo N:M, usar una hoja por tabla destino.'}
              </p>
              <p>
                <strong>Transformations:</strong> {t('wizard.mapping.import_format_transforms') || 'Separar con " → ". Incluir parámetros entre paréntesis. Ej: SET_FIXED(100) → TRIM_EDGES → UPPER'}
              </p>
              <p>
                <strong>Ignored:</strong> {t('wizard.mapping.import_format_ignored') || 'Colocar YES para excluir el campo de la migración.'}
              </p>
              <p className="text-blue-600 font-medium">
                <a
                  href="https://docs.google.com/spreadsheets/d/1nacgUsH8rCefloNGQXbmUzGXHGCS5X0x9yuup5GA464"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-800"
                >
                  📥 {t('wizard.mapping.import_download_template') || 'Descargar plantilla de ejemplo'}
                </a>
              </p>
            </div>
          </details>
        </div> */}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition"
          >
            {t('common.buttons.cancel') || 'Cancelar'}
          </button>
          <button
            onClick={onConfirm}
            disabled={!hasMatches}
            className="px-6 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {t('wizard.mapping.import_apply') || 'Aplicar'} ({summary.matched})
          </button>
        </div>
      </div>
    </div>
  );
}
